import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import { calculatePriceBasedOnFat, calculatePriceBasedOnFatAndSNF } from "../../../utils/calculatePrice";
import milkEntryService from "../../../services/milkEntryService";
import { printReceipt as printReceiptShared } from "../../../utils/printReceipt";

export function useMilkEntry({ userId, role, Name, t, navigate }) {
  const [inputs, setInputs] = useState({
    date: "",
    weight: "",
    fat: "",
    snf: "",
    price: "",
  });

  const [time, setTime] = useState(t("milkEntry.toggles.morning"));
  const [animalType, setAnimalType] = useState(t("milkEntry.toggles.cow"));
  const [calculationType, setCalculationType] = useState(t("milkEntry.toggles.fat"));

  const [isAutoIncrement, setIsAutoIncrement] = useState(
    () => localStorage.getItem("autoIncrement") === "true"
  );

  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);

  const [showPriceWarning, setShowPriceWarning] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [showReceiptAfterSubmit, setShowReceiptAfterSubmit] = useState(() => {
    const saved = localStorage.getItem("showReceiptAfterSubmit");
    return saved === null ? true : saved === "true";
  });

  const weightRef = useRef();
  const fatRef = useRef();
  const snfRef = useRef();
  const priceRef = useRef();

  const getLogicValues = () => {
    return {
      animal: animalType === t("milkEntry.toggles.buffalo") ? "buffalo" : "cow",
      calc:
        calculationType === t("milkEntry.toggles.fatSnf")
          ? "Fat And Snf"
          : "fat",
      timeShift: time === t("milkEntry.toggles.evening") ? "evening" : "morning",
    };
  };

  // Set today's date
  useEffect(() => {
    const todayDate = new Date().toISOString().split("T")[0];
    setInputs((prev) => ({ ...prev, date: todayDate }));
  }, []);

  // Reset toggle labels when language changes
  useEffect(() => {
    setAnimalType(t("milkEntry.toggles.cow"));
    setTime(t("milkEntry.toggles.morning"));
    setCalculationType(t("milkEntry.toggles.fat"));
  }, [t]);

  useEffect(() => {
    localStorage.setItem("showReceiptAfterSubmit", showReceiptAfterSubmit);
  }, [showReceiptAfterSubmit]);

  // Auto price calculation
  useEffect(() => {
    let calculatedPrice = "";
    const { animal, calc } = getLogicValues();

    if (calc === "fat") {
      calculatedPrice = calculatePriceBasedOnFat(animal, inputs.fat);
    } else if (calc === "Fat And Snf") {
      calculatedPrice = calculatePriceBasedOnFatAndSNF(animal, inputs.fat, inputs.snf);
    }

    setInputs((prev) => ({ ...prev, price: calculatedPrice }));
  }, [inputs.fat, inputs.snf, calculationType, animalType, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const finalTotal =
    inputs.weight && inputs.price
      ? (parseFloat(inputs.weight) * parseFloat(inputs.price)).toFixed(2)
      : "0.00";

  const handleRedirectToSettings = () => {
    const { animal } = getLogicValues();

    if (animal === "cow") {
      navigate("/price/cow_Milk_Prices");
    } else {
      navigate("/price/buffalo_Milk_Prices");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (userId === "" && role === "admin") {
      setIsOpen(true);
      return;
    }

    if (!inputs.price || parseFloat(inputs.price) <= 0) {
      setShowPriceWarning(true);
      return;
    }

    const { animal, timeShift } = getLogicValues();

    const receiptPayload = {
      ...inputs,
      time,
      animalType,
      total: finalTotal,
      calculationType,
      userName: Name,
    };

    try {
      const { updatedInputs, newDate } = await toast.promise(
        milkEntryService(
          { ...inputs, animalType: animal },
          timeShift,
          isAutoIncrement,
          userId
        ),
        {
          pending: t("milkEntry.messages.submitting"),
          success: t("milkEntry.messages.success"),
          error: t("milkEntry.messages.failed"),
        }
      );

      setInputs((prev) => ({
        ...prev,
        ...updatedInputs,
        date: isAutoIncrement && newDate ? newDate : prev.date,
      }));

      if (role === "admin" && showReceiptAfterSubmit) {
        setReceiptData(receiptPayload);
        setShowReceiptPopup(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

const printReceipt = () => printReceiptShared({ receiptData, receiptRef, t });

  const shareReceipt = async () => {
    // same logic as before
  };

  return {
    inputs,
    time,
    setTime,
    animalType,
    setAnimalType,
    calculationType,
    setCalculationType,
    isAutoIncrement,
    setIsAutoIncrement,
    showReceiptAfterSubmit,
    setShowReceiptAfterSubmit,
    showReceiptPopup,
    setShowReceiptPopup,
    receiptData,
    receiptRef,
    showPriceWarning,
    setShowPriceWarning,
    isOpen,
    setIsOpen,
    weightRef,
    fatRef,
    snfRef,
    priceRef,
    handleChange,
    finalTotal,
    handleSubmit,
    handleRedirectToSettings,
    printReceipt,
    shareReceipt,
  };
}